import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SltIsDuplicateService } from '../../services/slt-is-duplicate.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-slt-is-duplicate',
    templateUrl: './slt-is-duplicate.component.html',
    styleUrls: ['./slt-is-duplicate.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltIsDuplicateComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'Duplicate Data';
  minimise = false;
  loading = false;
  showTables = false;
  noSubmissionId = false;
  noRecords = false;

  // Component-specific properties
  sltIsDuplicateData: any = {
    P_S3_URL_DUP_DATA: [],
    P_CORP_DUP_DATA: [],
    P_PSHIP_DUP_DATA: [],
    P_CONSOL_DUP_DATA: [],
    P_MISSING_XML_DATA: []
  };

  // Global settings
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  filtersCount: number = 0;

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private sltIsDuplicateService: SltIsDuplicateService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    const fcRaw = activeScreen?.data?.filtersCount ?? activeScreen?.data?.filters_count;
    const fcNum = Number(fcRaw);
    this.filtersCount = Number.isFinite(fcNum) ? fcNum : 0;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Load duplicate data
        this.getDuplicateData();
      }
    } catch (e) {
      console.error('Error in SltIsDuplicate init:', e);
      this.loading = false;
    }
  }

  /**
   * Get duplicate data from API
   */
  getDuplicateData() {
    // Check if SUBMISSION_ID exists
    if (!this.rowData.SUBMISSION_ID) {
      this.loading = false;
      this.noSubmissionId = true;
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    // 🔴 CRITICAL: Determine isConsolDupData flag based on filters count from AngularJS
    // If filters.length > 1, set to 'N', otherwise 'Y' (matches AngularJS logic exactly)
    const isConsolDupData = this.filtersCount > 1 ? 'N' : 'Y';

    this.sltIsDuplicateService.getSltDuplicateData(
      this.GENERAL_CONFIG.base_url,
      this.rowData.TAX_YEAR,
      this.rowData.LE_KEY,
      this.rowData.SUBMISSION_ID,
      isConsolDupData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == "1") {
          this.sltIsDuplicateData = response;

          // Check if all data arrays are empty
          const hasNoData = (
            (!response.P_S3_URL_DUP_DATA || response.P_S3_URL_DUP_DATA.length === 0) &&
            (!response.P_CONSOL_DUP_DATA || response.P_CONSOL_DUP_DATA.length === 0) &&
            (!response.P_CORP_DUP_DATA || response.P_CORP_DUP_DATA.length === 0) &&
            (!response.P_MISSING_XML_DATA || response.P_MISSING_XML_DATA.length === 0) &&
            (!response.P_PSHIP_DUP_DATA || response.P_PSHIP_DUP_DATA.length === 0)
          );

          if (hasNoData) {
            this.showTables = false;
            this.noRecords = true;
          } else {
            this.showTables = true;
            this.noRecords = false;
          }

          this.loading = false;
          this.cdr.detectChanges();
        } else {
          this.loading = false;
          this._snackBar.open(response.message || 'Error loading duplicate data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading duplicate data:', error);
        this._snackBar.open('Error loading duplicate data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
